'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';

import { EPlatform } from '@/utils/enums/platform-enum';
import { useContext, useEffect, useState } from 'react';
import FaqAccordion from '../components/FaqAccordion';

import { env } from '@/utils/env';
import { priceInfo } from '@/utils/price';
import ActivityProductGrid from '../components/ActivityProductGrid';
import { IProduct } from './components/CollectionItemPcB';
import usePageTrack from './hooks/usePageTrack';

interface ServerInitialData {
  seoInfo: any;
  seoData: any;
  error: string | null;
  recommend_config: {
    title: string;
    group_id: string;
  };
  pg_description: {
    title_config: {
      title: string;
      title_color: string;
    };
    description_config: {
      description: string;
      description_color: string;
    };
    bg_color: string;
    image: string;
  };
  faq: {
    title: string;
    data_array?: Array<{
      question: string;
      answer: string;
    }>;
  };
}

interface ClientPageProps {
  serverData: ServerInitialData;
  exchangeRateList: Array<{ name?: string; exchange_rate?: string }>;
}

export default function PGClientPage({ serverData, exchangeRateList }: ClientPageProps) {
  // 使用hook获取数据，传入服务端预获取的数据
  const { recommend_config } = serverData;
  const { userInfo, currency } = useStore();
  const { trackProduct, trackPageMktClick } = usePageTrack({});
  const [hasMore, setHasMore] = useState(true);
  // App相关状态管理
  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType);
  const isWeb = deviceType === EPlatform.WEB;

  const exchangeRate =
    exchangeRateList.find(
      (item) => item.name === (currency.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY),
    )?.exchange_rate || '1';
  // * 商品曝光/点击埋点
  const reportProduct = (product: IProduct, index: number, type: 'exp' | 'click' = 'exp') => {
    const { short_name, id, sku, shop_sku, business_sku } = product;
    const { price, sell_price } = priceInfo(userInfo, product, currency);
    trackProduct(type, {
      module_name: '商品推荐',
      pits_index: index,
      product_name: short_name,
      product_id: id,
      product_sku: sku,
      business_sku,
      shop_sku,
      dollar_exchange_rate: exchangeRate,
      currency: currency.name,
      product_price: Number(price),
      base_current_product_price: Number(price),
      current_product_price: Number(sell_price),
    });
  };

  // 组件卸载时清理App通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  return (
    <>
      {/* Banner 区域 */}
      {/* {pageData?.banner && (
        <Banner bannerData={pageData.banner} ts={1} from="zeelool-x-prabal-gurung" />
      )} */}

      {/* 推荐商品 - 统一处理 */}
      <div className="bg-[#f4f4f4]">
        <ActivityProductGrid
          title={
            <div className={`pt-[24px] lg:pt-[54px] pb-[16px] lg:pb-[30px] mx-[30px]`}>
              <h4 className="text-[20px] lg:text-[32px] font-bb text-center leading-[24px] lg:leading-[48px]">
                {recommend_config.title}
              </h4>
            </div>
          }
          apiConfig={{
            group_id: recommend_config.group_id,
            limit: 12,
            in_stock: 3,
            is_sale: 3,
          }}
          className={isWeb ? 'bg-white' : 'rounded-t-[16px] bg-white'}
          showInfiniteScroll
          onLoadComplete={setHasMore}
          padding={{ wap: 'px-[8px]', pc: 'px-[48px]' }}
          productCardProps={{
            showCorrelation: true,
          }}
          deviceType={deviceType}
          onProductClick={(product, index) => {
            logger.log('PG Product clicked:', product, index);
            reportProduct(product as IProduct, index, 'click');
          }}
          onProductExp={(product, index) => {
            logger.log('PG Product exposed:', product, index);
            reportProduct(product as IProduct, index);
          }}
        />
      </div>

      {/* FAQ 区域 - 对应Vue中的"FAQ"曝光 */}
      {!hasMore && serverData?.faq && (
        <div
          className={`bg-[#f4f4f4] ${deviceType === EPlatform.WAP ? 'rounded-t-[10px]' : ''}`}
          onClick={() => trackPageMktClick({ module_name: 'FAQ' })}
        >
          <FaqAccordion faqData={serverData.faq} />
        </div>
      )}
    </>
  );
}
